import React from 'react';
import { Avatar, Tooltip } from 'antd';
import classNames from 'classnames';
import { ReactComponent as DrawingToolSvg } from '@assets/icons/drawing-tool.svg';
import { ReactComponent as ReplySvg } from '@assets/icons/reply-comment.svg';
import { ReactComponent as BlankSvg } from '@assets/icons/blank.svg';
import { ReactComponent as HeartSvg } from '@assets/icons/heart.svg';
import { AssetVersionCommentDto } from '@api/Api';
import { isJsonString } from '@helpers/isJsonString';
import UserAvatar from '@components/UserAvatar';
import { combineUserName } from '@helpers/combineUserName';

type BuildActionsParams = {
  comment: AssetVersionCommentDto & { children?: AssetVersionCommentDto[] };
  taskId: string | null;
  canLeaveComment: boolean;
  canLikeComment: boolean;
  onSelectComment: () => void;
  onOpenTask: () => void;
  onReply: () => void;
  onLikeClick: () => void;
};

export function buildCommentActions({
  comment,
  taskId,
  canLeaveComment,
  canLikeComment,
  onSelectComment,
  onOpenTask,
  onReply,
  onLikeClick
}: BuildActionsParams): React.ReactNode[] {
  const actions: React.ReactNode[] = [];
  const hasAnnotations = comment.annotations?.length;
  const hasDocumentHighlight = !!comment.documentHighlight?.length;

  if (
    (comment.annotations &&
      isJsonString(comment.annotations) &&
      JSON.parse(comment.annotations).length > 0 &&
      comment.annotations.indexOf('type') >= 0 &&
      hasAnnotations) ||
    hasDocumentHighlight
  ) {
    actions.push(
      <Tooltip
        key="has-annotations"
        overlayClassName="media_viewer_tooltip media_viewer_tooltip-small media_viewer_tooltip-no-border"
        title={
          comment.isCopied
            ? 'Cannot be displayed on this asset'
            : 'Go to comment'
        }
        placement="bottom"
      >
        <div onClick={onSelectComment}>
          <span className="hasAnnotations">
            <DrawingToolSvg />
          </span>
        </div>
      </Tooltip>
    );
  }

  if (taskId) {
    actions.push(
      <div key="has-task">
        <span className="hasTask">
          <span className="task-tag" onClick={onOpenTask}>
            <BlankSvg />
            Task
          </span>
        </span>
      </div>
    );
  }

  if (canLeaveComment) {
    actions.push(
      <div key="comment-reply">
        <span onClick={onReply} className="comment-reply">
          <span>Reply</span>
          <ReplySvg />
        </span>
      </div>
    );
  }

  const hasLike = comment.likes.find((el: any) => el.me);
  const countLikes = comment.likes?.length || 0;
  const showLikeBtn = canLikeComment || countLikes > 0;
  actions.push(
    <div
      key="comment-like"
      className={classNames(hasLike && 'liked', 'comment-like')}
    >
      {showLikeBtn && (
        <span
          onClick={onLikeClick}
          className="comment-like-icon"
          style={!canLikeComment ? { pointerEvents: 'none' } : undefined}
        >
          <HeartSvg />
        </span>
      )}
      {countLikes !== 0 && (
        <Tooltip
          overlayClassName="media_viewer_tooltip"
          className="comment-like-number"
          title={comment.likes.map((like: any) => (
            <Avatar.Group
              key={like.id}
              maxCount={9}
              size="small"
              className="comment-like_avatar-group"
              prefixCls="ant-avatar-group"
              maxPopoverTrigger="click"
            >
              <UserAvatar
                isActive={true}
                size={22}
                src={like.picture || ''}
                userEmail={like.email || ''}
                userName={combineUserName(like)}
              />
            </Avatar.Group>
          ))}
          placement="bottom"
        >
          {countLikes}
        </Tooltip>
      )}
    </div>
  );

  if (comment.isCopied) {
    actions.push(
      <div key="comment-copied" className="b-copy-comment">
        <Tooltip
          overlayClassName="media_viewer_tooltip media_viewer_tooltip-small media_viewer_tooltip-no-border"
          title="Comment duplicated from another asset"
          placement="top"
        >
          <span className="comment-copied">Copied</span>
        </Tooltip>
      </div>
    );
  }

  return actions;
}
